/**
 * Compliance (COMPLY-1) — what is due, what has been certified, and the
 * sheets the tests are done against.
 *
 * A security business's real liability is not an unpaid invoice, it is a
 * system that was never tested and a certificate nobody issued. Generic field
 * software treats a form as an attachment; here a submitted sheet rolls the
 * asset's next-due date and issues the numbered certificate, so this screen is
 * mostly about what has *not* happened yet.
 *
 * Which is why "Due" is the first tab and overdue never drops off it. A list
 * that quietly forgets a test that was due in March is how a four-hour-response
 * site goes eighteen months without a service.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ApiError, api, openDocument } from "../lib/api";
import { day } from "../lib/format";
import { Card, Chip, DataTable, Empty, ErrorState, Loading, PageHeader, Tile, Tiles } from "../components/ui";
import type { CertificateOut, FormTemplateOut, TestDueRow } from "../lib/types";

type Tab = "due" | "certificates" | "sheets";

export default function Compliance() {
  const [tab, setTab] = useState<Tab>("due");
  const due = useQuery({
    queryKey: ["tests-due", 60],
    queryFn: () => api.forms.due({ within_days: 60 }),
  });

  const rows = due.data ?? [];
  const overdue = rows.filter((r) => r.days_overdue > 0);

  return (
    <div className="page">
      <PageHeader title="Compliance">
        Test sheets, the certificates they issue, and what is due. A passing sheet books the next test by itself.
      </PageHeader>

      <Tiles>
        <Tile
          label="Overdue"
          value={overdue.length}
          detail={overdue.length ? "tests past their due date" : "nothing past due"}
          tone={overdue.length ? "crit" : "ok"}
        />
        <Tile
          label="Due within 60 days"
          value={rows.length - overdue.length}
          detail="worth putting on a run"
          tone={rows.length - overdue.length ? "warn" : undefined}
        />
      </Tiles>

      <div className="segmented" role="group" aria-label="Compliance">
        {(["due", "certificates", "sheets"] as Tab[]).map((t) => (
          <button key={t} type="button" className={tab === t ? "active" : ""} onClick={() => setTab(t)}>
            {t === "due" ? "Due" : t === "certificates" ? "Certificates" : "Test sheets"}
          </button>
        ))}
      </div>

      {tab === "due" && <Due query={due} />}
      {tab === "certificates" && <Certificates />}
      {tab === "sheets" && <Sheets />}
    </div>
  );
}

/* --------------------------------------------------------------------- due */

function Due({ query }: { query: ReturnType<typeof useQuery<TestDueRow[]>> }) {
  if (query.isPending) return <Loading what="Working out what's due" />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const rows = query.data;
  if (rows.length === 0) {
    return (
      <Card>
        <Empty title="Nothing due in the next 60 days">
          Every asset with a test interval has been tested inside its interval.
        </Empty>
      </Card>
    );
  }

  return (
    <Card padded={false}>
      <DataTable
        columns={[
          { key: "asset", label: "Asset" },
          { key: "site", label: "Site" },
          { key: "standard", label: "Standard", hideOnPhone: true },
          { key: "last", label: "Last tested", hideOnPhone: true },
          { key: "due", label: "Due" },
          { key: "sheet", label: "Sheet", hideOnPhone: true },
        ]}
      >
        <>
          {rows.map((row) => (
            <tr key={row.asset_id}>
              <td>
                <strong>{row.ref}</strong>
                {(row.manufacturer || row.model) && (
                  <div className="tiny muted">{[row.manufacturer, row.model].filter(Boolean).join(" ")}</div>
                )}
              </td>
              <td>
                <Link to={`/sites/${row.site_id}`}>{row.site_name}</Link>
              </td>
              <td className="tiny">{row.standard ?? "—"}</td>
              <td className="tiny">{row.last_test_on ? day(row.last_test_on) : "never"}</td>
              <td>
                {day(row.next_test_due_on)}{" "}
                {row.days_overdue > 0 ? (
                  <Chip tone="crit">{row.days_overdue} days overdue</Chip>
                ) : (
                  <Chip tone="warn">due</Chip>
                )}
              </td>
              <td className="tiny">{row.template_name ?? "no sheet for this category"}</td>
            </tr>
          ))}
        </>
      </DataTable>
    </Card>
  );
}

/* ------------------------------------------------------------ certificates */

function Certificates() {
  const [search, setSearch] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ["certificates", search.trim()],
    queryFn: () => api.certificates.list({ q: search.trim() || undefined }),
    placeholderData: (p) => p,
  });

  return (
    <>
      <div className="toolbar">
        <label className="search">
          <input
            type="search"
            value={search}
            placeholder="Certificate number or site"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}

      {list.isPending ? (
        <Loading what="Loading certificates" />
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.length === 0 ? (
        <Card>
          <Empty title="No certificates yet">
            Submitting a passing test sheet on a certifying template issues one, numbered and dated.
          </Empty>
        </Card>
      ) : (
        <Card padded={false}>
          <DataTable
            columns={[
              { key: "number", label: "Number" },
              { key: "site", label: "Site" },
              { key: "asset", label: "Asset", hideOnPhone: true },
              { key: "standard", label: "Standard", hideOnPhone: true },
              { key: "issued", label: "Issued" },
              { key: "next", label: "Next test", hideOnPhone: true },
              { key: "pdf", label: "" },
            ]}
          >
            <>
              {list.data.map((row: CertificateOut) => (
                <tr key={row.id}>
                  <td>
                    <strong className="mono">{row.number}</strong>
                  </td>
                  <td>
                    <Link to={`/sites/${row.site_id}`}>{row.site_name}</Link>
                  </td>
                  <td className="tiny">{row.asset_ref ?? "—"}</td>
                  <td className="tiny">{[row.standard, row.standard_class].filter(Boolean).join(" ") || "—"}</td>
                  <td className="tiny">{day(row.issued_on)}</td>
                  <td className="tiny">{row.valid_until ? day(row.valid_until) : "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="btn"
                      onClick={() =>
                        void openDocument(`/certificates/${row.id}/pdf`).catch((err) =>
                          setProblem(err instanceof ApiError ? err.message : "Couldn't open that certificate."),
                        )
                      }
                    >
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </>
          </DataTable>
        </Card>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ sheets */

function Sheets() {
  const list = useQuery({
    queryKey: ["form-templates"],
    queryFn: () => api.forms.templates(),
  });

  if (list.isPending) return <Loading what="Loading test sheets" />;
  if (list.error) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  if (list.data.length === 0) {
    return (
      <Card>
        <Empty title="No test sheets yet">
          A sheet is a list of questions plus what submitting it means: which standard it certifies against and how long
          until the next test.
        </Empty>
      </Card>
    );
  }

  return (
    <div className="split-main">
      {list.data.map((template: FormTemplateOut) => {
        const questions = (template.schema.sections ?? []).reduce((n, s) => n + (s.fields ?? []).length, 0);
        return (
          <Card key={template.id} title={template.name}>
            <div className="chips">
              <Chip>{template.code}</Chip>
              {template.standard && <Chip tone="info">{template.standard}</Chip>}
              {template.applies_to && <Chip>{template.applies_to}</Chip>}
              {template.produces_certificate ? <Chip tone="ok">issues a certificate</Chip> : <Chip>record only</Chip>}
              {template.sets_test_interval_months && (
                <Chip tone="warn">every {template.sets_test_interval_months} months</Chip>
              )}
              {template.schema.requires_signature && <Chip>signature required</Chip>}
            </div>
            <p className="tiny muted">
              {questions} question{questions === 1 ? "" : "s"} across {(template.schema.sections ?? []).length} section
              {(template.schema.sections ?? []).length === 1 ? "" : "s"}.
            </p>
          </Card>
        );
      })}
    </div>
  );
}
